import React from 'react';
import yaml from 'js-yaml';
// renderers is imported separately so Markdoc isn't sent to the client
import Markdoc, {renderers} from '@markdoc/markdoc'

import {getSchema, defaultObject} from '/vercel/path0/node_modules/@markdoc/next.js/src/runtime.js';
/**
 * Schema is imported like this so end-user's code is compiled using build-in babel/webpack configs.
 * This enables typescript/ESnext support
 */
const config = {};
import * as tags from '/vercel/path0/markdoc/tags/index.ts'
import * as nodes from '/vercel/path0/markdoc/nodes/index.ts'
const functions = {};
const schema = {
tags: defaultObject(tags),
nodes: defaultObject(nodes),
functions: defaultObject(functions),
...defaultObject(config),
};

const tokenizer = new Markdoc.Tokenizer();

/**
 * Source will never change at runtime, so parse happens at the file root
 */
const source = "---\ntitle: Delegator FAQ\npageTitle: Delegator FAQ | Covalent Network | Covalent\n---\n\n{% breadcrumb title=\"Delegator FAQ\" /%}\n\n# {% $markdoc.frontmatter.title %}\n\n### 1. How can I stake my CQT?\n\nStaking your CQT can be completed via the [Covalent Network Staking Dashboard](https://www.covalenthq.com/staking/). For a complete guide, please visit the guide 'How to stake CQT on the Covalent Network'.\n\n### 2. Can delegators choose any Network Operator?\n\nDelegators can choose to stake their tokens to any registered Covalent Network Operator. The main factors for delegators in choosing a Network Operator include the performance, the commission rate set by the Network Operator, and the reputation/experience of the Network Operator. After researching the Network Operators and understanding the risks involved, delegators can decide the number of tokens they want to delegate and which Network Operator they will delegate to.\n\n### 3. Can I swap my staked CQT to a different Operator?\n\nYes. You would first have to unstake your stake however and wait until the cool-down period is over.\n\n### 4. Why is staking live on Moonbeam?\n\nFor every piece of ‘work’ Network Operators perform such as producing a Block Specimen, a respective proof is published to the [ProofChain Contract](/docs/covalent-network/glossary/). Publishing proofs is a key mechanism in ensuring that the data Operators produce is honest and trustworthy. Publishing proofs on Ethereum Mainnet would have ridiculous costs. Hence, Moonbeam was selected as the settlement layer of the Covalent Network.  \n\n### 5. How often can rewards be claimed?\n\nRewards can be claimed immediately.\n\n### 6. Is there a minimum stake amount for delegators?\n\nThere is no minimum delegation amount. There is, however, a maximum cap that Operators can accept in delegations due to the MaxCap Multiplier staking parameter. Hence, some may be unavailable for delegating. This cap will be increasing with time however and as a result, more space will become available to stake with Network Operators.\n\n### 7. How can I bridge my CQT to Moonbeam?\n\nBridging is facilitated through Wormhole. To learn how to bridge your CQT to Moonbeam, check out the bridging guide [here](/docs/covalent-network/bridge/).   \n\n### 8. What are the risks of staking?\n\nGiven that slashing is not live on the Covalent Network yet, there is no risk of your delegated CQT being slashed.\n\nThe primary risk is that the staking contract is compromised. However, to mitigate this risk, the contract has undergone multiple audits. These can be found here:\n\n- [Security & Audits](/docs/covalent-network/audits/)\n\n### 9. Is there a cool-down period?\n\nThere is a cool-down period of 28 blocks. This only applies to the amount delegated and not your rewards available. Rewards can be redeemed immediately.\n\n### 10. How do delegator rewards work?\n\nIf a CQT token holder decides to stake CQT tokens with an Operator, the delegator will receive a portion of the rewards that the respective Operator has earned proportionate to the total amount staked. The delegator also has to consider the commission fee set by the Operator.\n\n### 11. Why do Operators charge a commission fee on staking rewards?\n\nThis fee is charged as Operators have to pay the expense of running the infrastructure necessary for operating on the Covalent Network.\n\n### 12. Is staking CQT non-custodial?\n\nWhile CQT is held in the staking contract, it is only the owner of the respective staked CQT that can interact with it.\n";
const filepath = "/docs/covalent-network/delegator-faq.md";
const tokens = tokenizer.tokenize(source);
const ast = Markdoc.parse(tokens);

/**
 * Like the AST, frontmatter won't change at runtime, so it is loaded at file root.
 * This unblocks future features, such a per-page dataFetchingFunction.
 */
const frontmatter = ast.attributes.frontmatter
  ? yaml.load(ast.attributes.frontmatter)
  : {};

const {components, ...rest} = getSchema(schema)

export async function getStaticProps(context) {
  const partials = {};

  // Ensure Node.transformChildren is available
  Object.keys(partials).forEach((key) => {
    const tokens = tokenizer.tokenize(partials[key]);
    partials[key] = Markdoc.parse(tokens);
  });

  const cfg = {
    ...rest,
    variables: {
      ...(rest ? rest.variables : {}),
      // user can't override this namespace
      markdoc: {frontmatter},
      // Allows users to eject from Markdoc rendering and pass in dynamic variables via getServerSideProps
      ...(context.variables || {})
    },
    partials,
    source,
  };

  /**
   * transform must be called in dataFetchingFunction to support server-side rendering while
   * accessing variables on the server
   */
  const content = await Markdoc.transform(ast, cfg);

  return {
    // Removes undefined
    props: JSON.parse(
      JSON.stringify({
        markdoc: {
          content,
          frontmatter,
          file: {
            path: filepath
          }
        },
      })
    ),
  };
}

export default function MarkdocComponent(props) {
  // Only execute HMR code in development
  return renderers.react(props.markdoc.content, React, {
    components: {
      ...components,
      // Allows users to override default components at runtime, via their _app
      ...props.components,
    },
  });
}
